// Demo store for promo codes (REACT_APP_PROMO_DEMO=true): same shapes as the API,
// kept in this browser's localStorage so a demo works before the backend ships.
const STORAGE_KEY = "promoCodesDemo";
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

const daysAgo = (days) => new Date(Date.now() - days * 86400000).toISOString();
const daysAhead = (days) => new Date(Date.now() + days * 86400000).toISOString();

const seed = () => [
  {
    id: "demo-ramadan-20",
    code: "RAMADAN-20",
    description: "Q4 sales push, monthly plans only",
    percent_off: 20,
    duration_cycles: 3,
    billing_periods: ["MONTHLY"],
    plan_ids: null,
    max_redemptions: 50,
    redemption_count: 2,
    valid_from: daysAgo(10),
    expires_at: daysAhead(60),
    active: true,
    created_by: "sales@magicstamp.com",
    created_at: daysAgo(10),
    redemptions: [
      { id: "r1", account_urn: "5b6c7d8e-9f0a-4b1c-2d3e-4f5a6b7c8d9e", account_name: "Bean There Coffee", status: "ACTIVE", cycles_applied: 2, duration_cycles: 3, remaining_cycles: 1, created_at: daysAgo(40) },
      { id: "r2", account_urn: "9a8b7c6d-5e4f-4a3b-2c1d-0e9f8a7b6c5d", account_name: "Crumbs Bakery", status: "ACTIVE", cycles_applied: 1, duration_cycles: 3, remaining_cycles: 2, created_at: daysAgo(5) },
      { id: "r3", account_urn: null, status: "RESERVED", cycles_applied: 0, duration_cycles: 3, remaining_cycles: 3, created_at: daysAgo(0) },
    ],
  },
  {
    id: "demo-year15",
    code: "YEAR15",
    description: "Annual upgrade offer",
    percent_off: 15,
    duration_cycles: 1,
    billing_periods: ["ANNUALLY"],
    plan_ids: null,
    max_redemptions: null,
    redemption_count: 7,
    valid_from: null,
    expires_at: null,
    active: true,
    created_by: "ops@magicstamp.com",
    created_at: daysAgo(30),
    redemptions: [],
  },
  {
    id: "demo-launch-50",
    code: "LAUNCH-50",
    description: "Launch event (paused)",
    percent_off: 50,
    duration_cycles: 2,
    billing_periods: ["MONTHLY", "ANNUALLY"],
    plan_ids: null,
    max_redemptions: 10,
    redemption_count: 10,
    valid_from: daysAgo(90),
    expires_at: daysAgo(30),
    active: false,
    created_by: "sales@magicstamp.com",
    created_at: daysAgo(90),
    redemptions: [],
  },
];

const load = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(stored)) return stored;
  } catch {
    // fall through to the seed
  }
  const initial = seed();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
  return initial;
};

const save = (codes) => localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));

const withComputed = (promo) => ({
  ...promo,
  remaining_redemptions:
    promo.max_redemptions == null ? null : Math.max(0, promo.max_redemptions - promo.redemption_count),
});

const fail = (status, message) => {
  const error = new Error(message);
  error.response = { status, data: { message } };
  return Promise.reject(error);
};

const generateCode = () =>
  `MS-${Array.from({ length: 8 }, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join("")}`;

const DEMO_PLANS = [
  { id: "demo-plan-basic", name: "Basic" },
  { id: "demo-plan-growth", name: "Growth" },
];

const promoMock = {
  plans() {
    return Promise.resolve(DEMO_PLANS);
  },

  list({ page = 1, limit = 20, search = "", active } = {}) {
    const term = String(search).trim().toLowerCase();
    const rows = load()
      .filter((promo) => active === undefined || String(promo.active) === String(active))
      .filter((promo) =>
        !term ||
        [promo.code, promo.description, promo.created_by].some((value) => String(value || "").toLowerCase().includes(term)),
      )
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const start = (page - 1) * limit;
    return Promise.resolve({
      docs: rows.slice(start, start + limit).map(({ redemptions, ...promo }) => withComputed(promo)),
      pagination: { page, limit, totalItems: rows.length, totalPages: Math.ceil(rows.length / limit) },
    });
  },

  get(id) {
    const promo = load().find((item) => item.id === id);
    return promo ? Promise.resolve(withComputed(promo)) : fail(404, "Promo code not found");
  },

  create(input) {
    const codes = load();
    const code = input.code ? String(input.code).trim().toUpperCase() : generateCode();
    if (codes.some((promo) => promo.code === code)) return fail(409, "A promo code with this name already exists");
    const promo = {
      id: `demo-${Date.now()}`,
      code,
      description: input.description || null,
      percent_off: Number(input.percentOff),
      duration_cycles: Number(input.durationCycles),
      billing_periods: input.billingPeriods?.length ? input.billingPeriods : ["MONTHLY", "ANNUALLY"],
      plan_ids: input.planIds?.length ? input.planIds : null,
      max_redemptions: input.maxRedemptions ?? null,
      redemption_count: 0,
      valid_from: input.validFrom || null,
      expires_at: input.expiresAt || null,
      active: true,
      created_by: "you (demo)",
      created_at: new Date().toISOString(),
      redemptions: [],
    };
    save([promo, ...codes]);
    return Promise.resolve(withComputed(promo));
  },

  update(id, changes) {
    const codes = load();
    const index = codes.findIndex((item) => item.id === id);
    if (index < 0) return fail(404, "Promo code not found");
    const next = { ...codes[index] };
    if (changes.active !== undefined) next.active = changes.active;
    if (changes.maxRedemptions !== undefined) next.max_redemptions = changes.maxRedemptions;
    if (changes.expiresAt !== undefined) next.expires_at = changes.expiresAt;
    if (changes.description !== undefined) next.description = changes.description;
    codes[index] = next;
    save(codes);
    return Promise.resolve(withComputed(next));
  },
};

export default promoMock;
